import React from "react";
import LegalPage, { LegalSection } from "@/components/templates/LegalPage";
import { COMPANY, addressInline } from "@/data/company";

const EFFECTIVE = "February 2026";

const CookiePolicy = () => (
  <LegalPage
    testid="cookie-policy-page"
    title="Cookie Policy"
    description="Cookies and similar technologies used on the Lutrom Engineering Ltd website — including Google Analytics 4 and Crisp Chat — and how to control them."
    path="/cookie-policy"
    h1="Cookie Policy"
    effective={EFFECTIVE}
  >
    <p>
      This Cookie Policy explains what cookies and similar technologies are used on the
      Lutrom Engineering Ltd website (<a href="https://www.lutrom.co.uk">www.lutrom.co.uk</a>),
      what they do, and how you can control them. It should be read alongside our{" "}
      <a href="/privacy-policy">Privacy Policy</a>.
    </p>

    <LegalSection title="What are cookies?">
      <p>
        Cookies are small text files that a website stores on your device when you visit it.
        They are widely used to make websites work, or work more efficiently, and to provide
        analytics and functionality information to the site&apos;s owner. Similar technologies
        (such as pixels, local storage and web beacons) are used for the same broad purposes.
      </p>
    </LegalSection>

    <LegalSection title="Cookies and similar technologies we use">
      <p>The Lutrom Engineering website currently uses the following cookies and services:</p>

      <div className="mt-4 space-y-6">
        <div className="border border-[color:var(--lt-border-strong)] rounded-lg p-5">
          <p className="text-white font-semibold text-[15px]">
            Strictly necessary
          </p>
          <p className="mt-2 text-[14.5px]">
            A small number of cookies and local-storage items are used to enable core site
            functionality — for example, to remember which navigation menu is open on your
            current visit or to keep the site secure. These do not track you and cannot be
            switched off without breaking the site.
          </p>
        </div>

        <div className="border border-[color:var(--lt-border-strong)] rounded-lg p-5">
          <p className="text-white font-semibold text-[15px]">
            Analytics — Google Analytics 4 (GA4)
          </p>
          <p className="mt-2 text-[14.5px]">
            We use Google Analytics 4 to understand how visitors use our website — for example,
            which pages are most commonly visited, how visitors arrive at the site and how
            long they spend on it. GA4 sets cookies and processes information such as your IP
            address (used to derive approximate location — usually to city level), device and
            browser type, and pages viewed. Google is the data controller for the data
            collected via GA4 for its own purposes; see{" "}
            <a
              href="https://policies.google.com/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              Google&apos;s Privacy &amp; Terms
            </a>{" "}
            for more information.
          </p>
        </div>

        <div className="border border-[color:var(--lt-border-strong)] rounded-lg p-5">
          <p className="text-white font-semibold text-[15px]">Chat — Crisp Chat</p>
          <p className="mt-2 text-[14.5px]">
            We use Crisp Chat to provide the on-site chat widget so you can start a
            conversation with us during and outside business hours. If you send a message via
            Crisp, information such as your name, email (if you provide it), the content of
            your messages and technical connection information is processed by Crisp on our
            behalf so that we can respond. See{" "}
            <a href="https://crisp.chat/en/privacy/" rel="noopener noreferrer" target="_blank">
              Crisp&apos;s Privacy Policy
            </a>{" "}
            for more information.
          </p>
        </div>
      </div>

      <p>
        We do not use advertising cookies or third-party marketing pixels on this website.
      </p>
    </LegalSection>

    <LegalSection title="How we obtain your consent">
      <p>
        On your first visit to the site you will see a cookie consent banner
        offering three choices: <span className="text-white">Accept all</span>,{" "}
        <span className="text-white">Reject non-essential</span>, or{" "}
        <span className="text-white">Customise</span> (separate toggles for
        analytics and chat). Google Analytics 4 and Crisp Chat are only loaded
        after you make a choice that permits them — they do not run before
        consent, and rejecting them keeps them off entirely.
      </p>
      <p>
        You can change your choice at any time using the{" "}
        <span className="text-white">Cookie settings</span> link in the site
        footer, which reopens the same panel.
      </p>
    </LegalSection>

    <LegalSection title="How to control cookies">
      <p>You can control cookies in a number of ways:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>
          <strong className="text-white">Browser settings.</strong> Most modern browsers let
          you view, delete and block cookies from specific sites or all sites, and to browse in
          a &lsquo;private&rsquo; or &lsquo;incognito&rsquo; mode that limits cookie
          persistence. Refer to your browser&apos;s help pages for instructions.
        </li>
        <li>
          <strong className="text-white">Google Analytics opt-out.</strong> You can install the
          Google Analytics Opt-out Browser Add-on available from{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            rel="noopener noreferrer"
            target="_blank"
          >
            tools.google.com/dlpage/gaoptout
          </a>{" "}
          to prevent GA4 collecting information about your visits across all websites that use
          the service.
        </li>
        <li>
          <strong className="text-white">Do Not Track.</strong> If your browser is configured
          to send a Do Not Track (DNT) signal we honour it where our tooling supports it.
        </li>
      </ul>
      <p>
        Please note that if you block or delete cookies, some parts of the website may not work
        as intended (for example, the chat widget may not appear).
      </p>
    </LegalSection>

    <LegalSection title="Changes to this policy">
      <p>
        We may update this Cookie Policy from time to time to reflect changes to the services
        we use or to the law. The &ldquo;Last updated&rdquo; date at the top of this page
        indicates when the policy was last revised.
      </p>
    </LegalSection>

    <LegalSection title="Contact">
      <p>
        For any question about this Cookie Policy please contact:
      </p>
      <p className="text-white">
        {COMPANY.name}
        <br />
        {addressInline()}
        <br />
        <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a>
        <br />
        <a href={COMPANY.phone.tel}>{COMPANY.phone.display}</a>
      </p>
    </LegalSection>
  </LegalPage>
);

export default CookiePolicy;
